import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import './styles/UserSettings.css';
import API_CONFIG from './config';

const UserSettings = ({ handleCloseUserSettings }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("General");
    
    // Helper function to handle closing user settings
    const handleClose = () => {
        if (handleCloseUserSettings) {
            handleCloseUserSettings();
        } else {
            // If no close handler provided, navigate back to home
            navigate('/');
        }
    };
    // Account settings state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
    });
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDeleteChatConfirmation, setShowDeleteChatConfirmation] = useState(false);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [deleteStatus, setDeleteStatus] = useState(null);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [archivedChats, setArchivedChats] = useState([]);
    const [archiveModalLoading, setArchiveModalLoading] = useState(false);
    const [archiveModalError, setArchiveModalError] = useState(null);
    
    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };


    const handleChange = (event) => {
        setFormData({
            ...formData,
            [event.target.name]: event.target.value,
        });
    };

    const handleDeleteAllChats = async () => {
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteAllChats = async () => {
        try {
            setLoading(true);
            setDeleteStatus(null);
            
            const authToken = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');
            
            if (!userId) {
                throw new Error('User ID not found. Please log in again.');
            }
            
            // console.log(`Making request to: ${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.DELETE_ALL}`);
            // console.log('Request payload:', { user_id: userId });
            
            // Update API call to use POST instead of DELETE if that's what the backend expects
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.DELETE_ALL}`, {
                method: 'POST', // Changed from DELETE to POST, as many APIs prefer POST for delete operations
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ user_id: userId })
            });
            
            // console.log('Response status:', response.status);
            // console.log('Response headers:', Object.fromEntries([...response.headers.entries()]));
            
            // Check if response is ok before trying to parse JSON
            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                // console.log('Content type:', contentType);
                
                // Only try to parse as JSON if the content type is JSON
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    // console.log('Error data:', errorData);
                    throw new Error(errorData.message || `Failed to delete chats (${response.status})`);
                } else {
                    // If not JSON, try to get text response for better error details
                    try {
                        const textResponse = await response.text();
                        // console.log('Error response text:', textResponse);
                        throw new Error(`Server error: ${response.status} ${response.statusText}`);
                    } catch (e) {
                        throw new Error(`Server error: ${response.status} ${response.statusText}`);
                    }
                }
            }
            
            // Try to parse as JSON, but handle if it's not JSON
            let data;
            try {
                // Check if response body is empty
                const clonedResponse = response.clone();
                const text = await clonedResponse.text();
                
                if (!text || text.trim() === '') {
                    // console.log('Response body is empty, considering successful');
                    data = { success: true };
                } else {
                    data = JSON.parse(text);
                    // console.log('Delete response:', data);
                }
            } catch (e) {
                // If parsing fails, it's ok as long as status was 200
                // console.log('Response is not valid JSON but status is OK:', e);
            }
            
            setDeleteStatus('All chats deleted successfully');
            
            // Reload the page after a short delay
            setTimeout(() => {
                window.location.reload();
            }, 1500);
            
        } catch (error) {
            console.error('Error deleting chats:', error);
            setDeleteStatus(`Error: ${error.message}`);
        } finally {
            setLoading(false);
            setShowDeleteConfirmation(false);
        }
    };

    const cancelDeleteAllChats = () => {
        setShowDeleteConfirmation(false);
    };

    const handleLogout = async () => {
        try {
            setLoading(true);
            const authToken = localStorage.getItem('authToken');
            
            // Call the logout API endpoint
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.LOGOUT}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    refresh_token: localStorage.getItem('refresh_token')
                })
            });
            
            if (!response.ok) {
                console.error('Logout API request failed');
            } else {
                // console.log('Successfully logged out from server');
            }
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            // Clear all authentication tokens regardless of API response
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('authToken');
            localStorage.removeItem('userId');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userName');
            // Reset free chat count on logout
            localStorage.removeItem('geoHubFreeChatCount');
            
            // Close settings and refresh page to redirect to login
            handleClose();
            window.location.href = '/';
            setLoading(false);
        }
    };

    const fetchArchivedChats = async () => {
        setArchiveModalLoading(true);
        setArchiveModalError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');
            
            // Changed to use HISTORY endpoint instead of GET_CHATS
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.ARCHIVE_CHATS}?user_id=${userId}`, {
                method: 'GET', // Changed to POST method as required by the HISTORY endpoint
                headers: { 
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                // body: JSON.stringify({
                //     user_id: userId
                // })
            });
            
            if (!response.ok) throw new Error('Failed to fetch archived chats');
            
            const data = await response.json();
            // console.log('Fetched history data:', data);
            
            // Extract conversations from the response
            const allConversations = Array.isArray(data.conversations) 
                ? data.conversations 
                : Array.isArray(data) 
                    ? data 
                    : [];
            
            // console.log('All conversations:', allConversations);
            
            // Filter to only include conversations with is_archived flag set to true
            const archivedConversations = allConversations.filter(convo => {
                // Handle different possible locations for is_archived
                const isArchived = convo.is_archived || 
                                  convo.conversations?.is_archived || 
                                  convo.archived || 
                                  false;
                return !!isArchived; // Ensure it's a boolean true
            });
            
            // console.log('Filtered archived conversations:', archivedConversations);
            
            // Map to objects with only title and timestamp
            const simplifiedArchivedChats = archivedConversations.map(convo => {
                // Extract title from different possible locations
                const title = convo.conversations?.title || 
                             convo.title || 
                             (convo.chat_history && convo.chat_history[0]?.content) || 
                             'Untitled Conversation';
                
                // Extract timestamp from different possible locations
                const timestamp = convo.updated_at || convo.created_at || new Date().toISOString();
                
                // Extract conversation_id from different possible locations
                const conversationId = convo.conversation_id || convo.id || convo._id;
                
                // console.log('Mapped archived chat:', {
                //     id: conversationId,
                //     title: title,
                //     timestamp: timestamp
                // });
                
                return {
                    chat_id: conversationId,       // Keep chat_id for backward compatibility 
                    conversation_id: conversationId, // Add conversation_id for API calls
                    title: title,
                    created_at: timestamp
                };
            });
            
            // console.log('Simplified archived chats:', simplifiedArchivedChats);
            setArchivedChats(simplifiedArchivedChats);
        } catch (err) {
            console.error('Error fetching archived chats:', err);
            setArchiveModalError(err.message);
        } finally {
            setArchiveModalLoading(false);
        }
    };

    const handleOpenArchiveModal = () => {
        setShowArchiveModal(true);
        fetchArchivedChats();
    };

    const handleCloseArchiveModal = () => {
        setShowArchiveModal(false);
    };

    const handleUnarchiveChat = async (chatId) => {
        // Unarchive logic
        try {
            const authToken = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');
            const endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.ARCHIVE}`;
            
            // console.log('Unarchiving chat with ID:', chatId);
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ 
                    user_id: userId, 
                    chat_id: chatId,
                    is_archived: false  // Set to false to unarchive
                })
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to unarchive chat: ${response.status} ${response.statusText}`);
            }
            
            // console.log('Chat unarchived successfully');
            
            // Dispatch custom event to notify sidebar to refresh conversations
            const event = new CustomEvent('conversationUnarchived', {
                detail: {
                    conversationId: chatId,
                    userId: userId
                }
            });
            // console.log('Dispatching conversationUnarchived event:', event);
            window.dispatchEvent(event);
            
            // Refresh archived chats list
            fetchArchivedChats();
        } catch (err) {
            console.error('Error unarchiving chat:', err);
            alert('Error unarchiving chat: ' + err.message);
        }
    };

    const handleDeleteChat = (chatId) => {
        setChatToDelete(chatId);
        setShowDeleteChatConfirmation(true);
    };

    const confirmDeleteChat = async () => {
        try {
            setArchiveModalLoading(true);
            const authToken = localStorage.getItem('authToken');
            const userId = localStorage.getItem('userId');
            
            // console.log('Deleting chat with ID:', chatToDelete);
            
            // Check if the user is currently viewing this conversation
            const currentPath = window.location.pathname;
            const isCurrentlyViewing = currentPath === `/c/${chatToDelete}`;
            
            const endpoint = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.DELETE}/${chatToDelete}`;
            const response = await fetch(endpoint, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    user_id: userId
                })
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                throw new Error(`Failed to delete chat: ${response.status} ${response.statusText}`);
            }
            
            // console.log('Chat deleted successfully');
            
            // If user was viewing the deleted conversation, redirect to frontend URL using navigate
            if (isCurrentlyViewing) {
                navigate('/');
                return; // Exit early to prevent further processing
            }
            
            // Dispatch custom event to notify sidebar to refresh conversations
            const event = new CustomEvent('conversationDeleted', {
                detail: {
                    conversationId: chatToDelete,
                    userId: userId
                }
            });
            // console.log('Dispatching conversationDeleted event:', event);
            window.dispatchEvent(event);
            
            // Refresh archived chats list
            fetchArchivedChats();
        } catch (err) {
            console.error('Error deleting chat:', err);
            alert('Error deleting chat: ' + err.message);
        } finally {
            setArchiveModalLoading(false);
            setShowDeleteChatConfirmation(false);
            setChatToDelete(null);
        }
    };

    const cancelDeleteChat = () => {
        setShowDeleteChatConfirmation(false);
        setChatToDelete(null);
    };

    const renderSettingsContent = () => {
        switch (activeTab) {
            case "General":
                return (
                    <div className="settings-options">
                        {/* <div className="setting-row">
                            <div className="setting-label">Pinned Chats</div>
                            <button className="setting-button">Manage</button>
                        </div> */}
                        <div className="setting-row">
                            <div className="setting-label">Archive</div>
                            <button className="setting-button" onClick={handleOpenArchiveModal}>Manage</button>
                        </div>
                        <div className="setting-row">
                            <div className="setting-label">Delete all chats</div>
                            <button className="setting-button danger" onClick={handleDeleteAllChats} disabled={loading}>
                                {loading ? 'Processing...' : 'Delete all'}
                            </button>
                            {deleteStatus && <div className={deleteStatus.includes('Error') ? "error-message" : "success-message"}>{deleteStatus}</div>}
                        </div>
                        <div className="setting-row">
                            <div className="setting-label">Log out on this device</div>
                            <button className="setting-button danger" onClick={handleLogout} disabled={loading}>
                                {loading ? 'Logging out...' : 'Log out'}
                            </button>
                        </div>
                    </div>
                );
          
            case "Account Settings":
                return (
                    <div className="security-settings">
                        <div className="setting-row">
                            <div className="setting-label">Change Password</div>
                            <button className="setting-button">Change</button>
                        </div>
                        <div className="setting-row">
                            <div className="setting-label">Two-Factor Authentication</div>
                            <button className="setting-button">Enable</button>
                        </div>
                        <div className="setting-row">
                            <div className="setting-label">Session Management</div>
                            <button className="setting-button">Manage</button>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="info-content">
                        <p>Settings for {activeTab} will be displayed here.</p>
                    </div>
                );
        }
    };

    return (
        <div className="settings-overlay">
            <div className="settings-modal">
                <div className="settings-header">
                    <h2>User Settings</h2>
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>
                <div className="settings-body">
                    <div className="settings-sidebar">
                        <ul className="settings-nav">
                            {["General", ""].map((tab) => (
                                <li 
                                    key={tab}
                                    className={activeTab === tab ? "active" : ""}
                                    onClick={() => handleTabClick(tab)}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="settings-main-content">
                        {renderSettingsContent()}
                    </div>
                </div>
                <div className="settings-footer">
                    <button className="footer-button" onClick={handleClose}>
                        Cancel
                    </button>
                    
                </div>
            </div>
            
            {showDeleteConfirmation && (
                <div className="confirmation-dialog-overlay">
                    <div className="confirmation-dialog">
                        <h3>Delete All Chats</h3>
                        <p>Are you sure you want to delete all chats? This action is irreversible.</p>
                        <div className="confirmation-actions">
                            <button 
                                className="confirmation-button cancel" 
                                onClick={cancelDeleteAllChats}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="confirmation-button confirm" 
                                onClick={confirmDeleteAllChats}
                                disabled={loading}
                            >
                                {loading ? 'Deleting...' : 'Delete All'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showArchiveModal && (
                <div className="archive-modal-overlay">
                    <div className="archive-modal dark">
                        <div className="archive-modal-header">
                            <span className="archive-modal-title">Archived Chats</span>
                            <button className="archive-modal-close" onClick={handleCloseArchiveModal}>&times;</button>
                        </div>
                        <div className="archive-modal-table-header">
                            <span>Name</span>
                            <span>Date created</span>
                            <span style={{width: 48}}></span>
                        </div>
                        <div className="archive-modal-table-body">
                            {archiveModalLoading ? (
                                <div className="archive-modal-loading">Loading...</div>
                            ) : archiveModalError ? (
                                <div className="archive-modal-error">{archiveModalError}</div>
                            ) : archivedChats.length === 0 ? (
                                <div className="archive-modal-empty">No archived chats.</div>
                            ) : archivedChats.map(chat => (
                                <div className="archive-modal-row" key={chat.chat_id}>
                                    <span className="archive-modal-chat-title">
                                        <svg style={{verticalAlign:'middle',marginRight:6}} width="18" height="18" fill="none" stroke="#2196f3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="9" r="8"/><path d="M6 9l2 2 4-4"/></svg>
                                        <span style={{color:'#2196f3',cursor:'pointer'}}>{chat.title}</span>
                                    </span>
                                    <span className="archive-modal-date">{chat.created_at ? new Date(chat.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</span>
                                    <span className="archive-modal-actions">
                                        <button className="archive-modal-icon-btn" title="Unarchive" onClick={() => handleUnarchiveChat(chat.chat_id)}>
                                            <svg width="18" height="18" fill="none" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 17v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                        </button>
                                        <button className="archive-modal-icon-btn" title="Delete" onClick={() => handleDeleteChat(chat.chat_id)}>
                                            <svg width="18" height="18" fill="none" stroke="#f44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="14" height="12" rx="2"/><path d="M9 10v4M15 10v4M5 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"/></svg>
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {showDeleteChatConfirmation && (
                <div className="confirmation-dialog-overlay" style={{ zIndex: 1300 }}>
                    <div className="confirmation-dialog">
                        <h3>Delete Chat</h3>
                        <p>Are you sure you want to delete this chat? This action is irreversible.</p>
                        <div className="confirmation-actions">
                            <button 
                                className="confirmation-button cancel" 
                                onClick={cancelDeleteChat}
                                disabled={archiveModalLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="confirmation-button confirm" 
                                onClick={confirmDeleteChat}
                                disabled={archiveModalLoading}
                            >
                                {archiveModalLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserSettings;